import * as React from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import SectionHeading from './SectionHeading';
import SpotlightCard from './SpotlightCard';
import Reveal, { Stagger } from './motion/Reveal';

const ACCENTS = [
  '210, 98%, 55%',
  '265, 90%, 62%',
  '190, 95%, 50%',
  '150, 70%, 45%',
  '25, 95%, 55%',
  '0, 85%, 60%',
];

/**
 * Shared layout for legal-style pages (Terms, Privacy).
 * Renders a gradient header, a "last updated" chip, and each section as an
 * icon-badged spotlight card, plus a closing contact callout.
 */
export default function LegalPage({
  icon,
  eyebrow,
  title,
  highlight,
  intro,
  lastUpdated,
  sections = [],
  contact,
}) {
  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  return (
    <Box sx={{ position: 'relative', overflow: 'hidden' }}>
      {/* Decorative header glow */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 480,
          pointerEvents: 'none',
          background: (t) =>
            t.palette.mode === 'dark'
              ? 'radial-gradient(900px 400px at 50% -8%, hsla(210,98%,30%,0.35), transparent 62%), radial-gradient(700px 380px at 85% 5%, hsla(265,90%,45%,0.22), transparent 60%)'
              : 'radial-gradient(900px 400px at 50% -8%, hsla(210,98%,78%,0.5), transparent 62%), radial-gradient(700px 380px at 85% 5%, hsla(265,90%,82%,0.4), transparent 60%)',
        }}
      />

      <Container maxWidth="md" sx={{ position: 'relative', py: { xs: 14, sm: 20 } }}>
        {/* Big page icon */}
        <Reveal y={18}>
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: 3,
              display: 'grid',
              placeItems: 'center',
              mb: 3,
              color: '#fff',
              backgroundImage: 'linear-gradient(135deg, hsl(210,98%,52%), hsl(265,90%,60%))',
              boxShadow: '0 12px 32px hsla(220,90%,50%,0.4)',
              '& svg': { fontSize: 36 },
            }}
          >
            {icon}
          </Box>
        </Reveal>

        <SectionHeading eyebrow={eyebrow} title={title} highlight={highlight} subtitle={intro} maxWidth={760} />

        {lastUpdated && (
          <Reveal delay={0.16} y={14}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                mt: 3,
                px: 1.6,
                py: 0.7,
                borderRadius: 999,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: (t) =>
                  t.palette.mode === 'dark' ? 'hsla(210,90%,60%,0.06)' : 'hsla(210,90%,50%,0.05)',
              }}
            >
              <EventAvailableRoundedIcon sx={{ fontSize: 17, color: 'primary.main' }} />
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                Last updated: {lastUpdated}
              </Typography>
            </Box>
          </Reveal>
        )}

        {/* Sections */}
        <Stagger style={{ marginTop: 48 }} gap={0.08}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {sections.map((section, i) => {
              const accent = ACCENTS[i % ACCENTS.length];
              return (
                <SpotlightCard key={section.title} tilt={false}>
                  <Box sx={{ p: { xs: 3, sm: 3.5 }, display: 'flex', gap: { xs: 2, sm: 2.5 } }}>
                    <Box
                      sx={{
                        flexShrink: 0,
                        width: 50,
                        height: 50,
                        borderRadius: 2,
                        display: 'grid',
                        placeItems: 'center',
                        color: `hsl(${accent})`,
                        background: `linear-gradient(135deg, hsla(${accent},0.18), hsla(${accent},0.04))`,
                        border: '1px solid',
                        borderColor: `hsla(${accent},0.3)`,
                        '& svg': { fontSize: 24 },
                      }}
                    >
                      {section.icon}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: '"JetBrains Mono", monospace',
                          fontWeight: 600,
                          letterSpacing: 1,
                          color: `hsl(${accent})`,
                        }}
                      >
                        {String(i + 1).padStart(2, '0')}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: '"Sora","Inter",sans-serif',
                          fontWeight: 700,
                          fontSize: '1.12rem',
                          mb: 1,
                          mt: 0.25,
                        }}
                      >
                        {section.title}
                      </Typography>
                      <Box
                        sx={{
                          color: 'text.secondary',
                          fontSize: '0.95rem',
                          lineHeight: 1.7,
                          '& ul': { pl: 2.5, mt: 1, mb: 0, display: 'flex', flexDirection: 'column', gap: 0.75 },
                          '& li::marker': { color: `hsl(${accent})` },
                          '& a': { color: 'primary.main', fontWeight: 600, textDecoration: 'none' },
                          '& a:hover': { textDecoration: 'underline' },
                        }}
                      >
                        {section.content}
                      </Box>
                    </Box>
                  </Box>
                </SpotlightCard>
              );
            })}
          </Box>
        </Stagger>

        {/* Contact callout */}
        {contact && (
          <Reveal delay={0.1} y={22}>
            <Box
              sx={{
                mt: 4,
                p: { xs: 3, sm: 4 },
                borderRadius: 3,
                position: 'relative',
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'hsla(210,98%,55%,0.35)',
                background: (t) =>
                  t.palette.mode === 'dark'
                    ? 'linear-gradient(120deg, hsla(210,98%,30%,0.22), hsla(265,90%,45%,0.14))'
                    : 'linear-gradient(120deg, hsla(210,98%,70%,0.28), hsla(265,90%,80%,0.2))',
              }}
            >
              <Typography
                sx={{ fontFamily: '"Sora","Inter",sans-serif', fontWeight: 700, fontSize: '1.2rem', mb: 0.75 }}
              >
                {contact.title}
              </Typography>
              <Box sx={{ color: 'text.secondary', lineHeight: 1.7, '& a': { color: 'primary.main', fontWeight: 600, textDecoration: 'none' }, '& a:hover': { textDecoration: 'underline' } }}>
                {contact.content}
              </Box>
            </Box>
          </Reveal>
        )}
      </Container>
    </Box>
  );
}
